import React, { useEffect, useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ChevronsUpDown, Check, Search, X, UserX } from "lucide-react";
import { useApp } from "../context/AppContext";

let _cache = null;
let _inflight = null;

/** Staff grouped by role (architects / drafters / pms / sales) + HR branches. Cached for instant render, re-fetched on every mount so newly created accounts show up. */
export function useStaffOptions() {
  const { api } = useApp();
  const [opts, setOpts] = useState(_cache);
  useEffect(() => {
    let alive = true;
    if (!_inflight) {
      _inflight = api.get("/hrms/staff-options").then((r) => { _cache = r.data; return r.data; }).catch(() => _cache || {}).finally(() => { _inflight = null; });
    }
    _inflight.then((d) => { if (alive) setOpts(d); });
    return () => { alive = false; };
  }, [api]);
  return opts || { architects: [], drafters: [], pms: [], sales: [], branches: [], loading: !_cache };
}

export function StaffPicker({ value, onChange, options = [], placeholder, lang = "zh", testid, disabled = false, hint, trigger, hideNone = false, exclude = [] }) {
  const zh = lang === "zh";
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = options.find((o) => o.name === value);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = exclude.length ? options.filter((o) => !exclude.includes(o.name)) : options;
    if (!s) return base;
    return base.filter((o) => [o.name, o.roleZh, o.roleEn, o.branchZh, o.branchEn, o.username].some((x) => (x || "").toLowerCase().includes(s)));
  }, [options, q, exclude]);
  const groups = useMemo(() => {
    const m = new Map();
    filtered.forEach((o) => { const k = (zh ? o.branchZh : o.branchEn) || (zh ? "其他" : "Other"); if (!m.has(k)) m.set(k, []); m.get(k).push(o); });
    return [...m.entries()];
  }, [filtered, zh]);
  const pick = (v) => { onChange(v); setOpen(false); setQ(""); };
  return (
    <>
    <Popover open={open} onOpenChange={(o) => { if (!disabled) { setOpen(o); if (!o) setQ(""); } }}>
      <PopoverTrigger asChild>
        {trigger || (
        <button type="button" disabled={disabled} data-testid={testid} data-value={value || ""}
          className="mt-1 flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm text-left disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-ring">
          {value ? (
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="shrink-0 font-medium text-slate-800">{value}</span>
              {selected
                ? <span className="text-[10px] text-slate-400 truncate">{zh ? selected.roleZh : selected.roleEn}</span>
                : <span className="text-[10px] text-amber-600 inline-flex items-center gap-0.5" title={zh ? "不在在职名单中（历史值）" : "Not in active roster (legacy value)"}><UserX className="h-3 w-3" />{zh ? "非在职名单" : "Not on roster"}</span>}
            </span>
          ) : <span className="text-slate-400">{placeholder || (zh ? "选择人员" : "Select")}</span>}
          <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400 shrink-0 ml-1" />
        </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px]" align="start" data-testid={testid ? `${testid}-list` : undefined}>
        <div className="flex items-center gap-1.5 border-b border-slate-100 px-2.5 py-2">
          <Search className="h-3.5 w-3.5 text-slate-400" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid={testid ? `${testid}-search` : undefined}
            placeholder={zh ? "搜索姓名 / 岗位 / 分公司…" : "Search name / role / branch…"}
            className="flex-1 text-sm bg-transparent outline-none placeholder:text-slate-400" />
          {q && <button type="button" onClick={() => setQ("")} className="text-slate-400 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>}
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {!hideNone && <button type="button" onClick={() => pick("")} data-testid={testid ? `${testid}-opt-none` : undefined}
            className="w-full text-left px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-50 flex items-center gap-2">
            <span className={`h-3.5 w-3.5 ${!value ? "text-red-600" : "text-transparent"}`}><Check className="h-3.5 w-3.5" /></span>{zh ? "未分配" : "Unassigned"}
          </button>}
          {groups.length === 0 && <p className="px-3 py-3 text-xs text-slate-400 text-center">{zh ? "没有匹配的在职员工" : "No matching active staff"}</p>}
          {groups.map(([g, items]) => (
            <div key={g}>
              <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{g}</p>
              {items.map((o) => (
                <button type="button" key={o.name} onClick={() => pick(o.name)} data-testid={testid ? `${testid}-opt-${o.username || o.name}` : undefined}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2 ${o.name === value ? "bg-red-50/60" : ""}`}>
                  <span className={`h-3.5 w-3.5 shrink-0 ${o.name === value ? "text-red-600" : "text-transparent"}`}><Check className="h-3.5 w-3.5" /></span>
                  <span className="truncate font-medium text-slate-800">{o.name}</span>
                  <span className="ml-auto text-[10px] text-slate-400 shrink-0">{zh ? o.roleZh : o.roleEn}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
    {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </>
  );
}
